import React from "react";
import PropTypes from "prop-types";
import { Box, Skeleton } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { colors } from "../../theme/colors";

export const profileSkeletonBoneSx = {
    bgcolor: alpha(colors.text.primary, 0.08),
};

const profileSkeletonPanelSx = {
    borderRadius: "10px",
    backgroundColor: alpha(colors.background.base, 0.75),
    border: `1px solid ${colors.border.default}`,
};

export function ProfileChartSkeleton({ height = "10rem", statCount = 0 }) {
    return (
        <Box sx={{ width: "100%", minWidth: 0 }}>
            <Skeleton
                variant="rounded"
                height={height}
                sx={{ ...profileSkeletonBoneSx, borderRadius: "12px" }}
            />
            {statCount > 0 ? (
                <Box sx={{ display: "flex", gap: "0.75rem", mt: "1.15rem" }}>
                    {Array.from({ length: statCount }).map((_, index) => (
                        <Skeleton
                            key={index}
                            variant="rounded"
                            height={64}
                            sx={{
                                flex: 1,
                                ...profileSkeletonBoneSx,
                                borderRadius: "14px",
                            }}
                        />
                    ))}
                </Box>
            ) : null}
        </Box>
    );
}

ProfileChartSkeleton.propTypes = {
    height: PropTypes.string,
    statCount: PropTypes.number,
};

export function ProfileMatchRowsSkeleton({ count = 5, showHeader = true }) {
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
                width: "100%",
            }}
        >
            {showHeader ? (
                <Skeleton
                    variant="rounded"
                    height={44}
                    sx={{
                        ...profileSkeletonBoneSx,
                        borderRadius: "10px",
                        display: { xs: "none", md: "block" },
                    }}
                />
            ) : null}
            {Array.from({ length: count }).map((_, index) => (
                <Box
                    key={index}
                    sx={{
                        ...profileSkeletonPanelSx,
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        alignItems: { xs: "stretch", md: "center" },
                        gap: { xs: "0.7rem", md: "0.75rem" },
                        px: { xs: "0.95rem", md: "1.15rem" },
                        py: { xs: "0.95rem", md: "0.65rem" },
                        minHeight: { xs: "9.5rem", md: "3.25rem" },
                        height: { xs: "auto", md: "3.25rem" },
                    }}
                >
                    <Skeleton
                        variant="text"
                        width="35%"
                        sx={{
                            ...profileSkeletonBoneSx,
                            display: { xs: "block", md: "none" },
                        }}
                    />
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.65rem",
                            width: "100%",
                        }}
                    >
                        <Skeleton
                            variant="rounded"
                            width={36}
                            height={36}
                            sx={{
                                ...profileSkeletonBoneSx,
                                borderRadius: "6px",
                                flexShrink: 0,
                            }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Skeleton
                                variant="text"
                                width="40%"
                                sx={profileSkeletonBoneSx}
                            />
                            <Skeleton
                                variant="text"
                                width="25%"
                                sx={{
                                    ...profileSkeletonBoneSx,
                                    display: { xs: "none", md: "block" },
                                }}
                            />
                        </Box>
                        <Skeleton
                            variant="rounded"
                            width={28}
                            height={24}
                            sx={{
                                ...profileSkeletonBoneSx,
                                borderRadius: "6px",
                                display: { xs: "none", md: "block" },
                            }}
                        />
                        <Skeleton
                            variant="text"
                            width={48}
                            sx={{
                                ...profileSkeletonBoneSx,
                                display: { xs: "none", md: "block" },
                            }}
                        />
                    </Box>
                    <Box
                        sx={{
                            display: { xs: "flex", md: "none" },
                            justifyContent: "space-between",
                            alignItems: "center",
                            pt: "0.35rem",
                            borderTop: "1px solid rgba(255,255,255,0.08)",
                        }}
                    >
                        <Skeleton
                            variant="rounded"
                            width={52}
                            height={28}
                            sx={{ ...profileSkeletonBoneSx, borderRadius: "6px" }}
                        />
                        <Skeleton
                            variant="text"
                            width={72}
                            sx={profileSkeletonBoneSx}
                        />
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

ProfileMatchRowsSkeleton.propTypes = {
    count: PropTypes.number,
    showHeader: PropTypes.bool,
};

export function ProfileMapRowsSkeleton({ count = 3 }) {
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem", width: "100%" }}>
            {Array.from({ length: count }).map((_, index) => (
                <Box
                    key={index}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem",
                        minWidth: 0,
                    }}
                >
                    <Skeleton
                        variant="rounded"
                        width={40}
                        height={40}
                        sx={{
                            ...profileSkeletonBoneSx,
                            borderRadius: "8px",
                            flexShrink: 0,
                        }}
                    />
                    <Box sx={{ width: "4.5rem", flexShrink: 0 }}>
                        <Skeleton variant="text" width="100%" sx={profileSkeletonBoneSx} />
                        <Skeleton variant="text" width="70%" sx={profileSkeletonBoneSx} />
                    </Box>
                    <Skeleton
                        variant="rounded"
                        height={6}
                        sx={{
                            flex: 1,
                            ...profileSkeletonBoneSx,
                            borderRadius: "999px",
                        }}
                    />
                    <Skeleton variant="text" width={36} sx={profileSkeletonBoneSx} />
                </Box>
            ))}
        </Box>
    );
}

ProfileMapRowsSkeleton.propTypes = {
    count: PropTypes.number,
};

export function ProfileActivityCalendarSkeleton() {
    const weeks = 13;

    return (
        <Box sx={{ overflowX: "auto", pb: "0.25rem" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                {Array.from({ length: 7 }).map((_, rowIndex) => (
                    <Box key={rowIndex} sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <Skeleton
                            variant="text"
                            width={17}
                            sx={{ ...profileSkeletonBoneSx, flexShrink: 0 }}
                        />
                        {Array.from({ length: weeks }).map((_, weekIndex) => (
                            <Skeleton
                                key={weekIndex}
                                variant="rounded"
                                width={17}
                                height={17}
                                sx={{
                                    ...profileSkeletonBoneSx,
                                    borderRadius: "3px",
                                    flexShrink: 0,
                                }}
                            />
                        ))}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

export function ProfileHeaderSkeleton() {
    return (
        <Box
            sx={{
                width: "100%",
                maxWidth: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "0.85rem",
                p: "1.15rem 1rem",
                borderRadius: "14px",
                border: "1px solid rgba(255,255,255,0.05)",
                boxSizing: "border-box",
            }}
        >
            <Skeleton
                variant="circular"
                width={112}
                height={112}
                sx={profileSkeletonBoneSx}
            />
            <Skeleton
                variant="text"
                width="70%"
                height={28}
                sx={profileSkeletonBoneSx}
            />
            <Skeleton
                variant="rounded"
                width={110}
                height={24}
                sx={{ ...profileSkeletonBoneSx, borderRadius: "4px" }}
            />
            <Skeleton variant="text" width="55%" sx={profileSkeletonBoneSx} />
            <Skeleton variant="text" width="65%" sx={profileSkeletonBoneSx} />
            <Skeleton
                variant="rounded"
                width="100%"
                height={72}
                sx={{ ...profileSkeletonBoneSx, mt: 0.5, borderRadius: "8px" }}
            />
        </Box>
    );
}

export function ProfileStatsBarSkeleton() {
    return (
        <Box
            sx={{
                width: "100%",
                display: "flex",
                alignItems: "stretch",
                overflow: "hidden",
                borderRadius: "24px",
                border: `1px solid ${colors.border.default}`,
                minHeight: "5.5rem",
                backgroundColor: alpha(colors.background.base, 0.75),
            }}
        >
            {Array.from({ length: 5 }).map((_, index) => (
                <Box
                    key={index}
                    sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "0.75rem",
                        px: "1.25rem",
                        py: "1.35rem",
                        borderRight:
                            index < 4 ? `1px solid ${colors.border.default}` : undefined,
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Skeleton variant="text" width="55%" sx={profileSkeletonBoneSx} />
                        <Skeleton
                            variant="text"
                            width="35%"
                            height={36}
                            sx={{ ...profileSkeletonBoneSx, mt: 1 }}
                        />
                        <Skeleton variant="text" width="45%" sx={profileSkeletonBoneSx} />
                    </Box>
                    <Skeleton
                        variant="rounded"
                        width={56}
                        height={40}
                        sx={{ ...profileSkeletonBoneSx, borderRadius: "8px", flexShrink: 0 }}
                    />
                </Box>
            ))}
        </Box>
    );
}
